import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { NavLink } from "react-router-dom";

import {
  CO_TAG_FULL_3_STEPS_CBCT,
  CO_TAG_TEEN_3_STEPS_CBCT,
  isAdditionalAlignersCourse,
  isChildrenShortCourse,
  isRetainersCourse,
} from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { Material, translateRxOption } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Button } from "~/components/ui/button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { selectServices } from "~/slices/services";
import { useAppSelector } from "~/store";
import type { TPatient } from "~/types/patient";
import type { TUser } from "~/types/user";

import { PatientCardAdditionalServices } from "./patient-card/patient-card-additional-services";
import { PatientCardClinic } from "./patient-card/patient-card-clinic";
import { PatientCardCourse } from "./patient-card/patient-card-course";
import { PatientCardCTFiles } from "./patient-card/patient-card-ct-files";
import { PatientCardLinks } from "./patient-card/patient-card-links";
import { PatientCardPatientInfo } from "./patient-card/patient-card-patient-info";
import { PatientCardPayment } from "./patient-card/patient-card-payment";
import { PatientCardPhotos } from "./patient-card/patient-card-photos";
import { PatientCardRecipe } from "./patient-card/patient-card-recipe";
import { PatientCardUploadedFiles } from "./patient-card/patient-card-uploaded-files";

function PatientCardErrorBoundary(props: React.ComponentProps<typeof PatientCard>) {
  return (
    <RemoteLogErrorBoundary component="patient_card">
      <PatientCard {...props} />
    </RemoteLogErrorBoundary>
  );
}

export { PatientCardErrorBoundary as PatientCard };

function PatientCard({ patient, user }: { patient: TPatient; user: TUser }) {
  const [selected, setSelected] = useState(patient.course.correction.length - 1);

  const services = useAppSelector(selectServices);
  const old_instructions = useAppSelector((state) => state.instructions);

  const { links = [], prescription } = patient.course.correction[selected] ?? {};
  const lastCorrection = getLastCorrection(patient);

  return (
    <Portlet id="patient-card">
      <PortletTitle
        iconClassName="icon-book-open"
        floatRightSlot={
          <Button variant="secondary" asChild>
            <NavLink to={`/pages/edit_patient/${patient.patient_id}`}>
              <FormattedMessage id="BTN_EDIT" />
            </NavLink>
          </Button>
        }
      >
        <FormattedMessage id="pat.view.header" />
      </PortletTitle>

      <div className="portlet-body" id="patient-block-body">
        <PatientCardPatientInfo patient={patient} />
        <PatientCardClinic patient={patient} />
        <PatientCardPayment patient={patient} />

        {!isAdditionalAlignersCourse(patient.course.course_id) &&
        (old_instructions.anthropometric_diagnostic ||
          services.length > 0 ||
          patient.course.course_option_tag == CO_TAG_FULL_3_STEPS_CBCT ||
          patient.course.course_option_tag == CO_TAG_TEEN_3_STEPS_CBCT) ? (
          <PatientCardAdditionalServices
            patient={patient}
            services={services}
            old_instructions={old_instructions}
          />
        ) : null}

        <PatientCardCourse patient={patient} />

        <h3 className="block" id="course-section">
          <FormattedMessage id="BLOCKHEAD_INSTRUCTIONS" />
        </h3>

        {isRetainersCourse(patient.course.course_id) ||
        isAdditionalAlignersCourse(patient.course.course_id) ? null : (
          <PatientCardTabs patient={patient} selected={selected} onSelectedChange={setSelected} />
        )}

        {prescription == null ? (
          <div>
            <FormattedMessage id="nocorrection" />
          </div>
        ) : (
          <div>
            {prescription.material ? (
              <p style={{ marginTop: 15, marginBottom: 0 }}>
                <FormattedMessage id={translateRxOption("material", prescription.material)} />
              </p>
            ) : null}

            {canOrderTestPlastic(user) && lastCorrection.test_plastic != null ? (
              <p style={{ marginTop: 15, marginBottom: 15 }}>
                <FormattedMessage
                  id={lastCorrection.test_plastic ? "C_TEST_PLASTIC_YES" : "C_TEST_PLASTIC_NO"}
                />
              </p>
            ) : null}

            {!isChildrenShortCourse(patient.course.course_id) &&
            patient.course.correction.length > 1 &&
            lastCorrection.with_ct != null ? (
              <p style={{ marginTop: 15, marginBottom: 15 }}>
                <FormattedMessage
                  id={lastCorrection.with_ct ? "C_YES_CT_CORRECTION" : "C_NO_CT_CORRECTION"}
                />
              </p>
            ) : null}

            <PatientCardRecipe
              user={user}
              course_id={patient.course.course_id}
              course_option_tag={patient.course.course_option_tag}
              prescription={prescription}
            />
          </div>
        )}

        {links.length > 0 ? <PatientCardLinks links={links} /> : null}

        {(isRetainersCourse(patient.course.course_id) &&
          prescription?.material == Material.IMPRESSIONS) ||
        isAdditionalAlignersCourse(patient.course.course_id) ? null : (
          <PatientCardUploadedFiles patient={patient} user={user} />
        )}

        {isRetainersCourse(patient.course.course_id) ||
        isAdditionalAlignersCourse(patient.course.course_id) ? null : (
          <>
            <PatientCardCTFiles />
            <PatientCardPhotos patient={patient} />
          </>
        )}

        <div className="form-actions">
          <Button variant="secondary" asChild>
            <NavLink to={`/pages/edit_patient/${patient.patient_id}`}>
              <FormattedMessage id="BTN_EDIT" />
            </NavLink>
          </Button>
        </div>
      </div>
    </Portlet>
  );
}

function PatientCardTabs({
  patient,
  selected,
  onSelectedChange,
}: {
  patient: TPatient;
  selected: number;
  onSelectedChange(newSelected: number): void;
}) {
  return (
    <div className="tw-flex tw-flex-wrap tw-gap-[5px]">
      {patient.course.correction.map((correction, index) => {
        return (
          <Button
            key={correction.id}
            variant={index == selected ? "default" : "empty"}
            className={
              index == selected
                ? ""
                : "tw-border-[#dcdcdc] tw-bg-[#dcdcdc] tw-text-white hover:tw-border-gray-400 hover:tw-bg-gray-400 active:tw-border-gray-400 active:tw-bg-gray-400"
            }
            onClick={() => onSelectedChange(index)}
          >
            {index == 0 ? (
              <FormattedMessage id="main.treatment" />
            ) : (
              <>
                <FormattedMessage
                  id={
                    isChildrenShortCourse(patient.course.course_id)
                      ? "pat.block.right.additional_course"
                      : "correction"
                  }
                />{" "}
                {index}
              </>
            )}
          </Button>
        );
      })}
    </div>
  );
}
